import { useState, useRef } from 'react';
import { ShoppingCart, LogIn, Phone, CreditCard, LayoutDashboard, MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Container } from '@/components/layout/container';
import { FloatingActions } from '@/components/layout/floating-actions';
import { Button } from '@/components/ui/button';
import { ProductGrid } from '@/features/products/components/product-grid';
import { ProductFilters } from '@/features/products/components/product-filters';
import { useProducts } from '@/features/products/hooks/use-products';
import { useProductFilterStore, useProductFilters } from '@/features/products/stores/product-filters';
import { useCart } from '@/features/cart/hooks/use-cart';
import { useCartMutations } from '@/features/cart/hooks/use-cart-mutations';
import { useAuth } from '@/features/auth/hooks/use-auth';
import { getDashboardRoute } from '@/features/auth/utils/role-utils';
import { getSupportPhone, useSettings } from '@/features/settings/hooks/use-settings';
import { useBranches } from '@/features/branches/hooks/use-branches';
import type { ProductDisplay, ProductVariant } from '@/features/products/types';

/**
 * Customer Catalog Page
 * 
 * Main shopping page displaying products with filtering and cart functionality.
 */
export function CatalogPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const filters = useProductFilters();
  const setPage = useProductFilterStore((state) => state.setPage);
  const { data: branches } = useBranches();
  const { data, isLoading } = useProducts(filters);
  const { items: cartItems } = useCart(user?.id);
  const { toggleCartProduct } = useCartMutations(user?.id);
  const { settings } = useSettings();
  const supportPhone = getSupportPhone();
  const cartSectionRef = useRef<HTMLDivElement>(null);
  const [isCartHighlighted, setIsCartHighlighted] = useState(false);
  const cartItemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  const handleAddToCart = async (product: ProductDisplay, variant?: ProductVariant) => {
    await toggleCartProduct({
      product,
      quantity: 1,
      variant,
    });
  };

  const handlePageChange = (page: number) => {
    setPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Scroll to cart section and trigger highlight animation
  const scrollToCart = () => {
    cartSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
    // Trigger highlight animation
    setIsCartHighlighted(true);
    setTimeout(() => setIsCartHighlighted(false), 3000);
  };

  return (
    <div className="min-h-screen bg-brand-50">
      <Container>
        {/* Banner Image - ONLY rendered when configured in settings */}
        {settings.banner_enabled && settings.banner_url && settings.banner_url.length > 0 && (
          <div className="-mt-4 h-48 w-full overflow-hidden rounded-b-3xl sm:h-56 md:-mt-8 md:h-80 lg:h-96">
            <img 
              src={settings.banner_url}                                    // ← No fallback
              alt={settings.banner_alt || 'Karebe Wines & Spirits'} 
              className="w-full h-full"
              style={{ objectFit: settings.banner_fit || 'cover' }}
            />
          </div>
        )}

        {/* Contact Info Bar */}
        <div className="mb-6 flex flex-col gap-3 rounded-2xl bg-brand-800 px-4 py-4 text-white shadow-md sm:mb-8 sm:flex-row sm:flex-wrap sm:items-center sm:justify-center sm:gap-6">
          {(() => {
            const displayBranch = branches?.find(b => b.is_main) || branches?.[0];
            
            if (displayBranch) {
              return (
                <div className="flex flex-col gap-3 border-white/20 pb-0 text-center sm:flex-row sm:items-center sm:gap-6 sm:text-left">
                  <div className="flex items-center justify-center gap-1 text-xs font-bold uppercase tracking-wider text-brand-200 sm:justify-start sm:text-sm">
                    {displayBranch.name} {displayBranch.is_main && <span className="bg-brand-600 text-white px-1.5 py-0.5 rounded text-[10px]">(Main)</span>}
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                    {displayBranch.phone && (
                      <div className="flex items-center justify-center gap-1.5 opacity-90 transition-opacity hover:opacity-100 sm:justify-start">
                        <Phone className="w-4 h-4" />
                        <a href={`tel:${displayBranch.phone.replace(/\s+/g, '')}`} className="font-medium text-sm hover:underline">{displayBranch.phone}</a>
                      </div>
                    )}
                    {displayBranch.mpesa_shortcode && (
                      <div className="flex items-center justify-center gap-1.5 rounded-md bg-brand-900/40 px-2 py-1 opacity-90 transition-opacity hover:opacity-100 sm:justify-start">
                        <CreditCard className="w-4 h-4" />
                        <span className="font-medium text-sm">Till: {displayBranch.mpesa_shortcode}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            }
            
            return (
              <>
                <div className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  <span className="font-medium">{supportPhone || 'Support number unavailable'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  <span className="font-medium">Till: 9137883</span>
                </div>
              </>
            );
          })()}
        </div>

        <div className="py-8">
          {/* Header */}
          <div className="mb-8 flex flex-col gap-4 rounded-3xl bg-white p-5 shadow-sm sm:p-6 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <h1 className="text-2xl font-display font-bold text-brand-900 sm:text-3xl">
                Product Catalog
              </h1>
              <p className="mt-1 text-sm text-brand-600 sm:text-base">
                Browse our collection of wines and spirits
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:w-auto">
              <Button
                variant="outline"
                onClick={scrollToCart}
                className="relative w-full justify-center"
              >
                <ShoppingCart className="mr-2 h-5 w-5" />
                Cart
                {cartItemCount > 0 && (
                  <span className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-brand-600 text-xs text-white">
                    {cartItemCount}
                  </span>
                )}
              </Button>
              <Button
                variant="ghost"
                onClick={() => user ? navigate(getDashboardRoute(user.role)) : navigate('/admin/login')}
                className="flex w-full items-center justify-center gap-2"
              >
                {user ? (
                  <>
                    <LayoutDashboard className="w-4 h-4" />
                    Dashboard
                  </>
                ) : (
                  <>
                    <LogIn className="w-4 h-4" />
                    Login
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="mb-6">
            <ProductFilters showSearch showCategory showSort />
          </div>

          {/* Product Grid */}
          <ProductGrid
            products={data?.data || []}
            isLoading={isLoading}
            onAddToCart={handleAddToCart}
            skeletonCount={8}
          />

          {/* Pagination */}
          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <Button
                variant="outline"
                onClick={() => handlePageChange(filters.page! - 1)}
                disabled={filters.page === 1}
              >
                Previous
              </Button>
              <span className="text-brand-600">
                Page {filters.page} of {data.totalPages}
              </span>
              <Button
                variant="outline"
                onClick={() => handlePageChange(filters.page! + 1)}
                disabled={filters.page === data.totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </Container>

      {/* Floating Action Buttons (includes cart button) */}
      <FloatingActions
        cartItemCount={cartItems.reduce((sum, item) => sum + item.quantity, 0)}
        isLoggedIn={!!user}
        userRole={user?.role}
        onAdminClick={() => navigate('/admin')}
        onRiderClick={() => navigate('/rider')}
        onLoginClick={() => navigate('/admin/login')}
      />

      {/* Cart Section - Main cart view */}
      <div 
        ref={cartSectionRef}
        id="cart-section"
        className={`py-8 bg-brand-50 transition-all duration-300 ${
          isCartHighlighted ? 'animate-cart-glow' : ''
        }`}
        tabIndex={-1}
        aria-label="Shopping cart"
      >
        <Container>
          {cartItems.length > 0 ? (
            <div className="rounded-2xl bg-white p-4 shadow-lg sm:p-6">
              <h2 className="mb-4 text-xl font-display font-bold text-brand-900 sm:text-2xl">
                Your Cart ({cartItemCount} items)
              </h2>
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex flex-col gap-3 rounded-xl bg-brand-50 p-3 sm:flex-row sm:items-center sm:gap-4">
                    <img
                      src={item.product.images[0] || '/placeholder-product.png'}
                      alt={item.product.name}
                      className="w-14 h-14 object-cover rounded-lg"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-brand-900 truncate">
                        {item.product.name}
                      </h3>
                      {item.variant && (
                        <p className="text-sm text-brand-500">{item.variant.volume}</p>
                      )}
                    </div>
                    <div className="text-left sm:text-right">
                      <p className="font-medium text-brand-900">
                        KES {(item.unitPrice * item.quantity).toLocaleString()}
                      </p>
                      <p className="text-sm text-brand-500">Qty: {item.quantity}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex flex-col gap-4 border-t border-brand-100 pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-brand-600">Total</p>
                  <p className="text-2xl font-bold text-brand-900">
                    KES {cartItems.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0).toLocaleString()}
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Button variant="outline" onClick={() => navigate('/cart')} className="w-full">
                    View Full Cart
                  </Button>
                  <Button onClick={() => navigate('/checkout')} className="w-full">
                    Checkout
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <ShoppingCart className="w-16 h-16 mx-auto text-brand-300 mb-4" />
              <p className="text-brand-600">Your cart is empty</p>
              <p className="text-sm text-brand-500 mt-1">Add items to see them here</p>
            </div>
          )}
        </Container>
        
        {/* Quick Action Buttons for Cart Section */}
        <Container>
          <div className="flex flex-wrap gap-3 justify-center mt-4">
            <Button
              variant="outline"
              className="gap-2"
              onClick={() => navigate('/checkout?action=call')}
            >
              <Phone className="w-4 h-4" />
              Checkout then Call
            </Button>
            <Button
              variant="outline"
              className="gap-2 bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
              onClick={() => navigate('/checkout?action=whatsapp')}
            >
              <MessageCircle className="w-4 h-4" />
              Checkout then WhatsApp
            </Button>
            {user ? (
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => navigate(getDashboardRoute(user.role))}
              >
                <LayoutDashboard className="w-4 h-4" />
                {user.role === 'rider' ? 'Rider Portal' : 'Admin Dashboard'}
              </Button>
            ) : (
              <Button
                variant="outline"
                className="gap-2"
                onClick={() => navigate('/admin/login')}
              >
                <LogIn className="w-4 h-4" />
                Admin/Rider Login
              </Button>
            )}
          </div>
        </Container>
      </div>
    </div>
  );
}

export default CatalogPage;
